import type {
  ColumnSizingState,
  Header,
  OnChangeFn,
  Table,
  Updater,
} from "@tanstack/react-table";

import type { AllowedExportFileTypes as AllowedExportFileTypes } from "@/app/app.interface";
import {
  ACTION_ID,
  ACTION_ID_COL_SIZE,
  LOADING_SKELETON_COL_SIZE,
  MIN_COLUMN_SIZE,
  SELECTOR_ID,
  SELECTOR_ID_COL_SIZE,
} from "@/app/constants/global-constants";
import type { Any as AnyValue, ExtendedColumnDef } from "@/interfaces/globals";
import { formatToCapitalizedWords } from "@/utils/core/generalUtils";

type HandleDownloadTableParams<T, V> = {
  columns: ExtendedColumnDef<T, V>[];
  data: T[];
  table: Table<T>;
  file: {
    name: string;
    type: AllowedExportFileTypes;
    sheet: string;
  };
};

type ExportableData = {
  headers: string[];
  data: string[][];
};

const SERIAL_NUMBER_HEADER = "S.N";
const SERIAL_NUMBER_COLUMN_WIDTH = 35;
const SERIAL_NUMBER_XLSX_COLUMN_WIDTH = 4;
const EXPORT_EXCLUDED_COLUMN_IDS = new Set<string>([SELECTOR_ID, ACTION_ID]);

const isSerialNumberHeader = (header: string | undefined): boolean => {
  return header === SERIAL_NUMBER_HEADER;
};

const normalizeCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const appendExtension = (fileName: string, extension: string): string => {
  return fileName.endsWith(extension) ? fileName : `${fileName}${extension}`;
};

const escapeCSVCell = (value: string): string => {
  return `"${value.replace(/"/g, '""')}"`;
};

const getSafeSheetName = (sheet: string): string => {
  const fallbackSheetName = "Sheet 1";
  const trimmedSheetName = (sheet || fallbackSheetName).trim();
  const sanitizedSheetName = trimmedSheetName
    .replace(/[\\/?*[\]:']/g, " ")
    .trim();

  return (sanitizedSheetName || fallbackSheetName).slice(0, 31);
};

const handleCSVExport = (data: ExportableData, fileName: string): void => {
  const rows = [data.headers, ...data.data].map((row) =>
    row.map((item) => escapeCSVCell(normalizeCellValue(item))).join(","),
  );

  const csv = rows.join("\r\n");
  const blob = new Blob(["\uFEFF", csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = appendExtension(fileName || "export", ".csv");
  a.click();

  URL.revokeObjectURL(url);
};

/**
 * Determines minimum and maximum width constraints for PDF table columns based on total column count.
 * More columns require tighter constraints to fit on the page.
 *
 * @param {number} columnCount - Total number of columns in the table
 * @returns {{min: number, max: number}} Object with min and max width values in points
 */
const getWidthConstraints = (
  columnCount: number,
): { min: number; max: number } => {
  if (columnCount > 15) {
    return { min: 60, max: 180 };
  }
  if (columnCount > 10) {
    return { min: 70, max: 200 };
  }

  return { min: 80, max: 250 };
};

/**
 * Calculates the ideal width for a PDF table column based on header and content length.
 * Uses character width estimation and averages content length to determine optimal size.
 *
 * @param {string} header - Column header text
 * @param {Array<string>} columnData - Array of all cell values in this column
 * @param {object} constraints - Min/max width boundaries
 * @param {number} constraints.min - Minimum allowed width
 * @param {number} constraints.max - Maximum allowed width
 * @returns {number} Calculated width in points, clamped to constraints
 */
const calculateIdealWidth = (
  header: string,
  columnData: string[],
  constraints: { min: number; max: number },
): number => {
  // Approximate width per character in points
  const CHAR_WIDTH = 6;
  const headerWidth = header.length * CHAR_WIDTH;
  const maxContentWidth = columnData.length
    ? Math.max(...columnData.map((cell) => String(cell).length * CHAR_WIDTH), 0)
    : 0;

  const ideal = Math.max(headerWidth * 1.1, maxContentWidth);

  return Math.max(constraints.min, Math.min(constraints.max, ideal));
};

/**
 * Calculates optimal column widths for PDF table export with adaptive scaling strategies.
 * Handles different scenarios: few columns, perfect fit, slight overflow, and major overflow.
 *
 * @param {Array<string>} headers - Array of column header names
 * @param {Array<Array<string>>} data - 2D array of table data
 * @param {number} pageWidth - Available page width in points (default: 814 for A4 landscape)
 * @returns {Array<number>} Array of column widths in points
 */
const calculateColumnWidths = (
  headers: string[],
  data: string[][],
  pageWidth: number = 814,
): number[] => {
  const columnCount = headers.length;
  const constraints = getWidthConstraints(columnCount);

  // Calculate ideal width for each column based on content
  const idealWidths = headers.map((header, idx) => {
    if (isSerialNumberHeader(header)) {
      return SERIAL_NUMBER_COLUMN_WIDTH;
    }

    return calculateIdealWidth(
      header,
      data.map((row) => row[idx] || ""),
      constraints,
    );
  });

  const totalWidth = idealWidths.reduce((sum, w) => sum + w, 0);

  // Few columns: distribute width evenly across page
  if (columnCount <= 6 && totalWidth < pageWidth) {
    const evenWidth = pageWidth / columnCount;

    return idealWidths.map((width, index) => {
      if (isSerialNumberHeader(headers[index])) {
        return width;
      }

      return Math.max(width, evenWidth * 0.9);
    });
  }

  // Perfect fit: use ideal widths as-is
  if (totalWidth <= pageWidth * 0.95) {
    return idealWidths;
  }

  // Slight overflow: scale down proportionally
  if (totalWidth <= pageWidth * 1.2) {
    const ratio = (pageWidth * 0.95) / totalWidth;

    return idealWidths.map((width, index) => {
      if (isSerialNumberHeader(headers[index])) {
        return width;
      }

      return Math.max(constraints.min, width * ratio);
    });
  }

  // Major overflow: apply aggressive cap to force fit
  return idealWidths.map((width, index) => {
    if (isSerialNumberHeader(headers[index])) {
      return width;
    }

    return Math.max(constraints.min, Math.min(width, 120));
  });
};

type ColumnChunk = { headers: string[]; data: string[][]; widths: number[] };

/**
 * Splits wide tables into multiple horizontal chunks for multi-page PDF export.
 * Each chunk repeats key columns (e.g., ID, Name) and includes a subset of remaining columns.
 * This prevents horizontal overflow by distributing columns across pages.
 *
 * @param {Array<string>} headers - Array of all column headers
 * @param {Array<Array<string>>} data - 2D array of table data
 * @param {Array<number>} columnWidths - Calculated width for each column
 * @param {object} config - Configuration with page width and key column count
 * @param {number} config.pageWidth - Width available for rendering columns
 * @param {number} config.keyColumnCount - Number of leading columns repeated in each chunk
 * @returns {Array<ColumnChunk>} Array of column chunks, each representing one PDF page
 */
const splitColumnsForPageBreak = (
  headers: string[],
  data: string[][],
  columnWidths: number[],
  config: { pageWidth: number; keyColumnCount: number } = {
    pageWidth: 814,
    keyColumnCount: 2,
  },
): ColumnChunk[] => {
  const { pageWidth, keyColumnCount } = config;
  const chunks: ColumnChunk[] = [];

  // Key columns (e.g., ID, Name) that repeat on every page
  const keyHeaders = headers.slice(0, keyColumnCount);
  const keyWidths = columnWidths.slice(0, keyColumnCount);
  const availableWidth = pageWidth - keyWidths.reduce((sum, w) => sum + w, 0);

  let chunkHeaders: string[] = [];
  let chunkWidths: number[] = [];
  let currentWidth = 0;

  // Helper to finalize and push a chunk
  const pushChunk = (): void => {
    const startColIndex = keyColumnCount + chunks.length * chunkHeaders.length;

    chunks.push({
      headers: [...keyHeaders, ...chunkHeaders],
      widths: [...keyWidths, ...chunkWidths],
      data: data.map((row) => [
        ...row.slice(0, keyColumnCount),
        ...row.slice(startColIndex, startColIndex + chunkHeaders.length),
      ]),
    });
  };

  // Iterate through non-key columns and group into chunks
  for (let i = keyColumnCount; i < headers.length; i++) {
    const colWidth = columnWidths[i];
    const wouldExceedWidth = currentWidth + colWidth > availableWidth;

    // Start a new chunk if adding this column would exceed page width
    if (wouldExceedWidth && chunkHeaders.length > 0) {
      pushChunk();
      chunkHeaders = [];
      chunkWidths = [];
      currentWidth = 0;
    }

    chunkHeaders.push(headers[i]);
    chunkWidths.push(colWidth);
    currentWidth += colWidth;
  }

  // Push the final chunk
  if (chunkHeaders.length > 0) {
    pushChunk();
  }

  return chunks;
};

const PDF_COLORS = {
  headerText: [255, 255, 255] as [number, number, number],
  bodyText: [0, 0, 0] as [number, number, number],
  borderLight: [209, 213, 219] as [number, number, number],
  border: [229, 231, 235] as [number, number, number],
  altRowBg: [249, 250, 251] as [number, number, number],
  sectionTitle: [75, 85, 99] as [number, number, number],
  pageNumber: [107, 114, 128] as [number, number, number],
};

const getTableStyles = (): {
  headStyles: AnyValue;
  bodyStyles: AnyValue;
  styles: AnyValue;
} => ({
  headStyles: {
    textColor: PDF_COLORS.headerText,
    fontStyle: "bold" as const,
    fontSize: 11,
    cellPadding: 6,
    halign: "left" as const,
    lineWidth: 0.5,
    lineColor: PDF_COLORS.borderLight,
  },
  bodyStyles: {
    fontSize: 10,
    cellPadding: 6,
    textColor: PDF_COLORS.bodyText,
    lineWidth: 0.5,
    lineColor: PDF_COLORS.border,
    halign: "left" as const,
  },
  styles: {
    overflow: "linebreak" as const,
    cellWidth: "wrap" as const,
    minCellHeight: 20,
  },
});

const createColumnStyles = (
  widths: number[],
): Record<number, { cellWidth: number }> =>
  widths.reduce(
    (acc, width, index) => {
      acc[index] = { cellWidth: width };

      return acc;
    },
    {} as Record<number, { cellWidth: number }>,
  );

const addPageNumbers = (doc: AnyValue, totalPages: number): void => {
  const { pageSize } = doc.internal;
  const pageHeight = pageSize.height || pageSize.getHeight();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(...PDF_COLORS.pageNumber);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageSize.width / 2,
      pageHeight - 10,
      {
        align: "center",
      },
    );
  }
};

/**
 * Renders a chunked table across multiple PDF pages.
 * Each chunk is rendered on a separate page with consistent styling.
 *
 * @param {AnyValue} doc - jsPDF document instance
 * @param {ColumnChunk[]} chunks - Array of column chunks from splitColumnsForPageBreak
 * @param {AnyValue} autoTable - jspdf-autotable plugin function
 * @returns {void} Does not return a value.
 */
const renderChunkedTable = (
  doc: AnyValue,
  chunks: ColumnChunk[],
  autoTable: AnyValue,
): void => {
  const styles = getTableStyles();

  chunks.forEach((chunk, index) => {
    // Add a new page for each chunk after the first
    if (index > 0) {
      doc.addPage();
    }

    const startY = 20;

    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.sectionTitle);

    autoTable(doc, {
      head: [chunk.headers],
      body: chunk.data,
      startY: startY + 15,
      margin: { top: 20, right: 14, bottom: 30, left: 14 },
      columnStyles: createColumnStyles(chunk.widths),
      ...styles,
      showHead: "everyPage",
    });
  });
};

/**
 * Renders a standard table (non-chunked) in the PDF with automatic horizontal page breaks.
 * Used when the table fits within page width constraints.
 *
 * @param {AnyValue} doc - jsPDF document instance
 * @param {ExportableData} data - Exportable table data with headers and rows
 * @param {number[]} columnWidths - Calculated column widths
 * @param {AnyValue} autoTable - jspdf-autotable plugin function
 * @returns {void} Does not return a value.
 */
const renderStandardTable = (
  doc: AnyValue,
  data: ExportableData,
  columnWidths: number[],
  autoTable: AnyValue,
): void => {
  const styles = getTableStyles();

  autoTable(doc, {
    head: [data.headers],
    body: data.data,
    startY: 20,
    margin: { top: 20, right: 14, bottom: 30, left: 14 },
    columnStyles: createColumnStyles(columnWidths),
    ...styles,
    horizontalPageBreak: true,
    horizontalPageBreakBehaviour: "afterAllRows",
    horizontalPageBreakRepeat: 0,
    showHead: "everyPage",
  });
};

/**
 * Exports table data to PDF format with intelligent column width management and chunking.
 * Dynamically imports jsPDF libraries, calculates optimal column widths, and chooses between
 * standard or chunked rendering based on table dimensions.
 *
 * Strategy:
 * - Tables with >15 columns and total width exceeding page width are split into chunks
 * - Each chunk repeats key columns (first 2) and includes subset of remaining columns
 * - Standard tables use horizontal page breaks for automatic overflow handling
 *
 * @param {ExportableData} data - Exportable table data with headers and row arrays
 * @param {string} fileName - Output filename (with or without .pdf extension)
 * @returns {Promise<void>} A promise that resolves after the PDF file is saved
 */
const handlePDFExport = async (
  data: ExportableData,
  fileName: string,
): Promise<void> => {
  // Dynamic import to reduce initial bundle size
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  // Initialize PDF document in landscape mode for wider tables
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = 814; // A4 landscape width in points
  const columnCount = data.headers.length;
  const columnWidths = calculateColumnWidths(
    data.headers,
    data.data,
    pageWidth,
  );
  const totalWidth = columnWidths.reduce((sum, w) => sum + w, 0);

  // Determine if table should be split horizontally across pages
  const shouldChunk = columnCount > 15 && totalWidth > pageWidth;

  if (shouldChunk) {
    // Split wide tables into multiple pages with repeating key columns
    const chunks = splitColumnsForPageBreak(
      data.headers,
      data.data,
      columnWidths,
      {
        pageWidth,
        keyColumnCount: 2, // Repeat first 2 columns on each page
      },
    );

    renderChunkedTable(doc, chunks, autoTable);
  } else {
    // Render standard table with automatic vertical pagination
    renderStandardTable(doc, data, columnWidths, autoTable);
  }

  // Add page numbers to all pages
  addPageNumbers(doc, doc.getNumberOfPages());
  doc.save(fileName || "export.pdf");
};

const handleTXTExport = (data: ExportableData, fileName: string): void => {
  const txtRows = [data.headers, ...data.data].map((row) =>
    row.map((item) => normalizeCellValue(item)).join(","),
  );

  const blob = new Blob([txtRows.join("\r\n")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = appendExtension(fileName || "export", ".txt");
  a.click();

  URL.revokeObjectURL(url);
};

const handleXLSXExport = async (
  data: ExportableData,
  fileName: string,
  sheet: string,
): Promise<void> => {
  const XLSX = await import("@e965/xlsx");

  const worksheetData = [data.headers, ...data.data].map((row) =>
    row.map((cell) => normalizeCellValue(cell)),
  );
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1:A1");
  const columnWidths = data.headers.map((header) => header.length);

  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];

      if (!cell) {
        continue;
      }

      const cellValue = cell.v as string;

      cell.v = cellValue;
      cell.t = "s";
      cell.z = "@";

      columnWidths[col] = Math.max(columnWidths[col] ?? 0, cellValue.length);
    }
  }

  worksheet["!cols"] = columnWidths.map((width, index) => {
    if (isSerialNumberHeader(data.headers[index])) {
      return { wch: SERIAL_NUMBER_XLSX_COLUMN_WIDTH };
    }

    return {
      wch: Math.min(Math.max(width + 2, 10), 60),
    };
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, getSafeSheetName(sheet));

  const finalFileName = appendExtension(fileName || "export", ".xlsx");

  XLSX.writeFile(workbook, finalFileName, {
    bookType: "xlsx",
    type: "file",
  });
};

const resolveDataOf = <TData>(
  key: keyof TData,
  data: TData,
): string | undefined => {
  if (!key || typeof key !== "string") {
    return undefined;
  }
  const keys = key.split(".");

  const getValue = (obj: AnyValue, keys: string[]): AnyValue => {
    if (keys.length === 0) {
      return JSON.stringify(obj);
    }
    const [currentKey, ...restKeys] = keys;

    if (!currentKey) {
      return getValue(obj, restKeys);
    }

    if (Array.isArray(obj[currentKey])) {
      return obj[currentKey]

        .map((item: AnyValue) => getValue(item, restKeys))
        .join(", ");
    }

    if (obj[currentKey] && typeof obj[currentKey] === "object") {
      return getValue(obj[currentKey], restKeys);
    }

    return obj[currentKey];
  };

  const result = getValue(data, keys);

  if (typeof result === "object" && result !== null) {
    return JSON.stringify(result);
  }

  return result;
};

export const getSelectedTableRows = <TData>(table: Table<TData>): string[] => {
  if (!table) {
    return [];
  }

  const selectedRowIds = table
    .getSelectedRowModel()
    .rows.map((row) => {
      const dataWithId = row.original as { id: string };

      if (dataWithId.id) {
        return dataWithId.id;
      }

      return undefined;
    })
    .filter((v) => v !== undefined) as string[];

  return selectedRowIds;
};

const parseExportableData = <TData, TValue>(
  columns: ExtendedColumnDef<TData, TValue>[],
  data: TData[],
  table: Table<TData>,
): ExportableData => {
  const selectedRowIds = getSelectedTableRows(table);

  const exportableColumns = columns.filter((column) => {
    if (column.id && EXPORT_EXCLUDED_COLUMN_IDS.has(column.id)) {
      return false;
    }

    if (!column.map) {
      return false;
    }

    if (!column.map.key) {
      return false;
    }

    return !column.map.excludeFromExport;
  });

  const headers = [
    SERIAL_NUMBER_HEADER,
    ...exportableColumns.map((column) =>
      formatToCapitalizedWords(column.map?.key ?? ""),
    ),
  ];

  const exportMappers = exportableColumns.map((column) => ({
    item: column.map?.valueFrom || column.map?.mergedValuesFrom,
    transformer: column.map?.dataTransformer,
  }));

  const filteredData = data.filter((row) => {
    if (selectedRowIds.length === 0) {
      return true;
    }

    const dataWithId = row as { id: string };

    return selectedRowIds.includes(dataWithId.id);
  });

  const resolvedData = filteredData.map((row, index) => {
    const rowData = exportMappers
      .map((exportMapper) => {
        if (exportMapper.transformer) {
          return exportMapper.transformer(row);
        }

        if (Array.isArray(exportMapper.item)) {
          const mergedValue = exportMapper.item
            .map((k) => {
              if (k !== undefined && typeof k === "string") {
                return resolveDataOf(k as keyof TData, row);
              }

              return undefined;
            })
            .filter((v) => v !== undefined)
            .join(", ");

          return mergedValue;
        }

        return resolveDataOf(exportMapper.item as keyof TData, row);
      })
      .map((v) => (v === undefined || v === null ? "" : String(v)));

    return [String(index + 1), ...rowData];
  });

  return {
    headers,
    data: resolvedData as string[][],
  };
};

export const handleDownloadTable = <TData, TValue>({
  columns,
  data,
  table,
  file,
}: HandleDownloadTableParams<TData, TValue>): Promise<void> => {
  const exportable = parseExportableData(columns, data, table);

  switch (file.type) {
    case "csv":
      handleCSVExport(exportable, file.name);

      return Promise.resolve();
    case "pdf":
      return handlePDFExport(exportable, file.name);
    case "txt":
      handleTXTExport(exportable, file.name);

      return Promise.resolve();
    case "xlsx":
      return handleXLSXExport(exportable, file.name, file.sheet);
    default:
      handleCSVExport(exportable, file.name);

      return Promise.resolve();
  }
};

export const getColumnWidth = <TData, TValue>(
  header: Header<TData, TValue>,
  isLoading?: boolean,
): number => {
  const isSelectId = header.column.columnDef.id === SELECTOR_ID;
  const isActionId = header.column.columnDef.id === ACTION_ID;

  if (isSelectId) {
    return SELECTOR_ID_COL_SIZE;
  }

  if (isActionId) {
    return ACTION_ID_COL_SIZE;
  }

  if (isLoading) {
    return LOADING_SKELETON_COL_SIZE;
  }

  const size = header.getSize();

  if (size <= MIN_COLUMN_SIZE) {
    return MIN_COLUMN_SIZE;
  }

  return size;
};

/**
 * Column size clamping logic.
 *
 * @param {(value: React.SetStateAction<ColumnSizingState>) => void} setColumnSizing - Column size updater
 * @returns {(updtr: Updater<ColumnSizingState>) => void} Updater Function
 */
export const onWPXColumnSizingChange = (
  setColumnSizing: (value: React.SetStateAction<ColumnSizingState>) => void,
): OnChangeFn<ColumnSizingState> | undefined => {
  const updaterFunction = (updtr: Updater<ColumnSizingState>): void => {
    setColumnSizing((previousSizingState) => {
      const nextSizingState =
        typeof updtr === "function" ? updtr(previousSizingState) : updtr;
      const clampedSizingState: typeof nextSizingState = {};

      Object.entries(nextSizingState).forEach(([columnId, size]) => {
        if (!Number.isFinite(size)) {
          clampedSizingState[columnId] = MIN_COLUMN_SIZE;

          return;
        }

        clampedSizingState[columnId] = Math.max(MIN_COLUMN_SIZE, size);
      });

      return clampedSizingState;
    });
  };
  return updaterFunction;
};
